import React, { useState } from "react";
import pluralize from "pluralize";

const WIRELESS_TYPE_NAMES = ["Wireless Transmitter", "Wireless Receiver"];
const POWER_SUBTYPE_NAMES = ["Power Conditioner", "Surge Protector", "UPS"];

const PLURAL_OVERRIDES = {
  "Amp / Cabinet": "Amps / Cabinets",
  "Keyboard / Synth": "Keyboards / Synths",
  "Stage Box / Splitter": "Stage Boxes / Splitters",
  Power: "Power",
  UPS: "UPS",
};

function pluralizeGroup(name) {
  return PLURAL_OVERRIDES[name] || pluralize(name);
}

const UTILITY_LABEL = "Utility";

// Maps device type names to a function that returns sub-category names for a device.
// Add entries here to enable sub-grouping chips for other device types in the future.
const SUB_CATEGORY_CONFIG = {
  Pedal: (device) => {
    const effects = (device.effect_types || []).map((e) => e.name);
    return effects.length > 0 ? effects : [UTILITY_LABEL];
  },
};

function isSystemDevice(device) {
  return !!device.device_system;
}

function getDeviceSystems(device) {
  if (!device.device_system) return [];
  return device.device_system.split(",").map((s) => s.trim());
}

// Normalize text for search: lowercase, strip non-alphanumeric (keep spaces)
function normalize(text) {
  return (text || "").toLowerCase().replace(/[^a-z0-9 ]/g, "");
}

// Build a single searchable string for a device
function searchableText(d) {
  return normalize(
    [
      d.display_name,
      d.name,
      d.manufacturer,
      ...(d.device_types || []).map((t) => t.name),
      ...(d.effect_types || []).map((e) => e.name),
      d.description,
      d.wireless_system && d.wireless_system.replace(/_/g, " "),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

// Score how well a device matches: higher = better
// 2 = display_name match, 1 = manufacturer match, 0 = other field match
function searchScore(d, terms) {
  const dName = normalize(d.display_name);
  const mfr = normalize(d.manufacturer);
  if (terms.every((t) => dName.includes(t))) return 2;
  if (terms.some((t) => dName.includes(t)) && terms.some((t) => mfr.includes(t))) return 2;
  if (terms.every((t) => mfr.includes(t))) return 1;
  return 0;
}

function groupDeviceSystems(devices) {
  const systems = {};
  devices.forEach((device) => {
    if (!isSystemDevice(device)) return;
    for (const key of getDeviceSystems(device)) {
      if (!systems[key]) {
        systems[key] = {
          deviceSystem: key,
          manufacturer: device.manufacturer || "",
          isWireless: !!device.wireless_system,
          devices: [],
        };
      }
      systems[key].devices.push(device);
      if (device.wireless_system) {
        systems[key].isWireless = true;
      }
    }
  });

  const MIC_TYPES = new Set(["Microphone"]);
  const POWER_TYPES = new Set(["Power", ...POWER_SUBTYPE_NAMES]);

  for (const key in systems) {
    const sys = systems[key];
    sys.devices.sort((a, b) => {
      // Wireless: TX before RX
      const aIsRx = (a.device_types || []).some((t) => t.name === "Wireless Receiver");
      const bIsRx = (b.device_types || []).some((t) => t.name === "Wireless Receiver");
      if (aIsRx !== bIsRx) return aIsRx ? 1 : -1;

      // Wired: Microphone first
      const aIsMic = (a.device_types || []).some((t) => MIC_TYPES.has(t.name));
      const bIsMic = (b.device_types || []).some((t) => MIC_TYPES.has(t.name));
      if (aIsMic !== bIsMic) return aIsMic ? -1 : 1;

      // Power last
      const aIsPower = (a.device_types || []).some((t) => POWER_TYPES.has(t.name));
      const bIsPower = (b.device_types || []).some((t) => POWER_TYPES.has(t.name));
      if (aIsPower !== bIsPower) return aIsPower ? 1 : -1;

      return a.name.localeCompare(b.name);
    });
  }

  return Object.values(systems).sort((a, b) =>
    a.manufacturer.localeCompare(b.manufacturer) ||
    a.deviceSystem.localeCompare(b.deviceSystem)
  );
}

// Find the first non-wireless type name across all devices in a system
function systemDisplayType(system) {
  for (const d of system.devices) {
    for (const t of d.device_types || []) {
      if (!WIRELESS_TYPE_NAMES.includes(t.name)) return t.name;
    }
  }
  // Fallback: use the first device's first type
  const first = system.devices[0];
  return (first.device_types || [])[0]?.name || "Other";
}

function systemDisplayName(system) {
  const mfrLower = system.manufacturer.toLowerCase().replace(/[^a-z0-9]/g, "");
  const slug = system.deviceSystem;
  const rest = slug.startsWith(mfrLower + "_")
    ? slug.slice(mfrLower.length + 1)
    : slug;
  const label = rest
    .split("_")
    .map((w) => w.toUpperCase())
    .join(" ");
  return `${system.manufacturer} ${label}`;
}

// Build type groups with systems embedded inline.
// Complete systems (2+ members) appear as sub-groups under their primary type.
// System members are excluded from individual listings in other type groups.
function buildGroupItems(devices, searchTerms) {
  const allSystems = groupDeviceSystems(devices);
  const completeSystems = allSystems.filter((sys) => sys.devices.length >= 2);

  // Collect IDs of devices in complete systems
  const systemDeviceIds = new Set(
    completeSystems.flatMap((sys) => sys.devices.map((d) => d.id))
  );

  // Assign each complete system to a type group
  const systemsByType = {};
  for (const sys of completeSystems) {
    const typeName = systemDisplayType(sys);
    systemsByType[typeName] = systemsByType[typeName] || [];
    systemsByType[typeName].push(sys);
  }

  // Group non-system devices by type (items are { type: "device", device })
  const groups = {};
  devices.forEach((device) => {
    if (systemDeviceIds.has(device.id)) return;

    const types = (device.device_types || []).filter(
      (t) => !WIRELESS_TYPE_NAMES.includes(t.name)
    );
    let key = types[0]?.name || "Other";
    if (POWER_SUBTYPE_NAMES.includes(key)) key = "Power";
    groups[key] = groups[key] || [];
    groups[key].push({ type: "device", device });
  });

  // Sort individual devices within each group
  for (const key in groups) {
    groups[key].sort((a, b) => {
      if (searchTerms && searchTerms.length > 0) {
        const scoreDiff = searchScore(b.device, searchTerms) - searchScore(a.device, searchTerms);
        if (scoreDiff !== 0) return scoreDiff;
      }
      const mfr = (a.device.manufacturer || "").localeCompare(b.device.manufacturer || "");
      return mfr !== 0 ? mfr : a.device.name.localeCompare(b.device.name);
    });
  }

  // Insert systems into their type groups
  for (const [typeName, systems] of Object.entries(systemsByType)) {
    groups[typeName] = groups[typeName] || [];
    for (const sys of systems) {
      groups[typeName].push({ type: "system", system: sys });
    }
  }

  return groups;
}

export default function DeviceCatalog({ devices }) {
  const [filter, setFilter] = useState("");
  const [activeType, setActiveType] = useState(null);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [expandedSystems, setExpandedSystems] = useState({});

  const typeNames = [
    ...new Set(
      devices.flatMap((d) => (d.device_types || []).map((t) => t.name))
    ),
  ]
    .filter((name) => !WIRELESS_TYPE_NAMES.includes(name))
    .filter((name) => !POWER_SUBTYPE_NAMES.includes(name))
    .sort();

  const hasWirelessDevices = devices.some((d) => !!d.wireless_system);
  const chipNames = [
    ...typeNames,
    ...(hasWirelessDevices ? ["Wireless"] : []),
  ];

  const initialFiltered = devices.filter((d) => {
    if (activeType === "Wireless") {
      if (!d.wireless_system) return false;
    } else if (activeType === "Power") {
      const dTypes = (d.device_types || []).map((t) => t.name);
      const matchesPower = dTypes.includes("Power") || dTypes.some((t) => POWER_SUBTYPE_NAMES.includes(t));
      if (!matchesPower) return false;
    } else if (activeType) {
      const matchesType = (d.device_types || []).some((t) => t.name === activeType);
      if (!matchesType) return false;
    }

    if (!filter) return true;

    const terms = normalize(filter).split(/\s+/).filter((t) => t.length > 0);
    if (terms.length === 0) return true;
    const haystack = searchableText(d);
    return terms.every((t) => haystack.includes(t));
  });

  // Include direct system companions when filtering narrows the device list
  // (text search or type chip). For multi-system devices, only include system
  // keys shared with another matched device -- prevents cascading.
  let filtered;
  if (filter || activeType) {
    const matchedWithSystems = initialFiltered.filter(isSystemDevice);
    const keyCount = {};
    for (const d of matchedWithSystems) {
      for (const key of getDeviceSystems(d)) {
        keyCount[key] = (keyCount[key] || 0) + 1;
      }
    }

    const matchedSystems = new Set();
    for (const d of matchedWithSystems) {
      const systems = getDeviceSystems(d);
      if (systems.length === 1) {
        matchedSystems.add(systems[0]);
      } else {
        const shared = systems.filter((key) => keyCount[key] >= 2);
        if (shared.length > 0) {
          shared.forEach((key) => matchedSystems.add(key));
        } else {
          systems.forEach((key) => matchedSystems.add(key));
        }
      }
    }

    const filteredIds = new Set(initialFiltered.map((d) => d.id));
    filtered = [
      ...initialFiltered,
      ...devices.filter(
        (d) => isSystemDevice(d) && getDeviceSystems(d).some((s) => matchedSystems.has(s)) && !filteredIds.has(d.id)
      ),
    ];
  } else {
    filtered = initialFiltered;
  }

  // Sub-category filtering (e.g. effect types within Pedal)
  const subCategoryFn = activeType && SUB_CATEGORY_CONFIG[activeType];
  const subCategoryNames = subCategoryFn
    ? [...new Set(filtered.flatMap((d) => subCategoryFn(d)))].sort()
    : [];

  if (activeSubCategory && subCategoryFn) {
    filtered = filtered.filter((d) =>
      subCategoryFn(d).includes(activeSubCategory)
    );
  }

  const isFiltering = !!(filter || activeType);
  const currentSearchTerms = filter ? normalize(filter).split(/\s+/).filter((t) => t.length > 0) : [];
  const groups = buildGroupItems(filtered, currentSearchTerms);

  const filterKey = `${filter}\0${activeType}`;

  function isGroupExpanded(groupName) {
    if (isFiltering) {
      const entry = expanded[groupName];
      if (entry && entry.filterKey === filterKey) return entry.open;
      return true;
    }
    return !!expanded[groupName];
  }

  function isSystemExpanded(systemKey) {
    if (isFiltering) {
      const entry = expandedSystems[systemKey];
      if (entry && entry.filterKey === filterKey) return entry.open;
      return true;
    }
    return !!expandedSystems[systemKey];
  }

  function onDragStart(event, device) {
    event.dataTransfer.setData("application/json", JSON.stringify(device));
    event.dataTransfer.effectAllowed = "move";
  }

  function onSystemDragStart(event, system) {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({ devices: system.devices })
    );
    event.dataTransfer.effectAllowed = "move";
  }

  function toggleGroup(groupName) {
    if (isFiltering) {
      const currentlyOpen = isGroupExpanded(groupName);
      setExpanded((prev) => ({ ...prev, [groupName]: { open: !currentlyOpen, filterKey } }));
    } else {
      setExpanded((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
    }
  }

  function toggleSystem(systemKey) {
    if (isFiltering) {
      const currentlyOpen = isSystemExpanded(systemKey);
      setExpandedSystems((prev) => ({ ...prev, [systemKey]: { open: !currentlyOpen, filterKey } }));
    } else {
      setExpandedSystems((prev) => ({ ...prev, [systemKey]: !prev[systemKey] }));
    }
  }

  function renderSystem(system) {
    return (
      <li key={system.deviceSystem} className="device-catalog-system">
        <div
          className="device-catalog-system-header"
          draggable
          onDragStart={(e) => onSystemDragStart(e, system)}
        >
          <button
            className="device-catalog-system-toggle"
            onClick={() => toggleSystem(system.deviceSystem)}
            aria-expanded={isSystemExpanded(system.deviceSystem)}
          >
            <span className="device-catalog-system-arrow">
              {isSystemExpanded(system.deviceSystem) ? "\u25bc" : "\u25b6"}
            </span>
          </button>
          <div className="device-catalog-system-info">
            <div className="device-catalog-system-name">
              {systemDisplayName(system)}
            </div>
            <div className="device-catalog-system-hint">
              Drag to add system
            </div>
          </div>
        </div>
        {isSystemExpanded(system.deviceSystem) && (
          <ul className="device-catalog-system-devices">
            {system.devices.map((device) => (
              <li
                key={device.id}
                className="device-catalog-item device-catalog-item--nested"
                draggable
                onDragStart={(e) => onDragStart(e, device)}
              >
                <div className="device-catalog-item-top">
                  <div className="device-catalog-item-name">
                    {device.slug ? (
                      <a href={`/devices/${device.slug}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{device.name}</a>
                    ) : device.name}
                  </div>
                  {device.slug && (
                    <a className="device-catalog-item-info" href={`/devices/${device.slug}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} aria-label={`View ${device.name} details`} title={device.description || "View details"}>
                      &#9432;
                    </a>
                  )}
                </div>
                <div className="device-catalog-item-bottom">
                  {device.manufacturer && (
                    <div className="device-catalog-item-manufacturer">
                      {device.manufacturer}
                    </div>
                  )}
                  <div className="device-catalog-item-grip" aria-hidden="true" title="Drag to add">&#x2630;</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  function renderDevice(device) {
    return (
      <li
        key={device.id}
        className="device-catalog-item"
        draggable
        onDragStart={(e) => onDragStart(e, device)}
      >
        <div className="device-catalog-item-top">
          <div className="device-catalog-item-name">
            {device.slug ? (
              <a href={`/devices/${device.slug}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{device.name}</a>
            ) : device.name}
          </div>
          {device.slug && (
            <a className="device-catalog-item-info" href={`/devices/${device.slug}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} aria-label={`View ${device.name} details`} title={device.description || "View details"}>
              &#9432;
            </a>
          )}
        </div>
        <div className="device-catalog-item-bottom">
          {device.manufacturer && (
            <div className="device-catalog-item-manufacturer">
              {device.manufacturer}
            </div>
          )}
          <div className="device-catalog-item-grip" aria-hidden="true" title="Drag to add">&#x2630;</div>
        </div>
      </li>
    );
  }

  return (
    <div className="device-catalog">
      <div className="device-catalog-header">
        <h2 className="device-catalog-title">Devices</h2>
        <input
          type="text"
          className="device-catalog-filter"
          placeholder="Search devices..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter devices"
        />
      </div>
      <div className="device-catalog-chips" role="group" aria-label="Filter by type">
        {(activeType ? chipNames.filter((name) => name === activeType) : chipNames).map((name) => (
          <button
            key={name}
            className={`device-catalog-chip${activeType === name ? " device-catalog-chip--active" : ""}`}
            aria-pressed={activeType === name}
            onClick={() => {
              setActiveType(activeType === name ? null : name);
              setActiveSubCategory(null);
            }}
          >
            {name}
          </button>
        ))}
      </div>
      {subCategoryNames.length > 0 && (
        <div className="device-catalog-chips" role="group" aria-label="Filter by sub-category">
          {(activeSubCategory ? subCategoryNames.filter((name) => name === activeSubCategory) : subCategoryNames).map((name) => (
            <button
              key={name}
              className={`device-catalog-chip device-catalog-chip--sub${activeSubCategory === name ? " device-catalog-chip--active" : ""}`}
              aria-pressed={activeSubCategory === name}
              onClick={() => setActiveSubCategory(activeSubCategory === name ? null : name)}
            >
              {name}
            </button>
          ))}
        </div>
      )}
      <div className="device-catalog-groups">
        {Object.entries(groups)
          .sort(([a, aItems], [b, bItems]) => {
            if (activeType && activeType !== "Wireless") {
              if (a === activeType) return -1;
              if (b === activeType) return 1;
            }
            if (currentSearchTerms.length > 0) {
              const bestScore = (items) => items.reduce((max, item) => {
                const d = item.type === "system" ? item.system.devices[0] : item.device;
                return d ? Math.max(max, searchScore(d, currentSearchTerms)) : max;
              }, 0);
              const scoreDiff = bestScore(bItems) - bestScore(aItems);
              if (scoreDiff !== 0) return scoreDiff;
            }
            return a.localeCompare(b);
          })
          .map(([groupName, items]) => (
            <div key={groupName} className="device-catalog-group">
              <button
                className="device-catalog-group-header"
                onClick={() => toggleGroup(groupName)}
                aria-expanded={isGroupExpanded(groupName)}
              >
                <span>{isGroupExpanded(groupName) ? "\u25bc" : "\u25b6"}</span>
                <span>{pluralizeGroup(groupName)}</span>
              </button>
              {isGroupExpanded(groupName) && (
                <ul className="device-catalog-list">
                  {items.map((item) =>
                    item.type === "system"
                      ? renderSystem(item.system)
                      : renderDevice(item.device)
                  )}
                </ul>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
